import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import InputNumberAtom from '../../../atoms/InputNumber/InputNumberAtom';
import type { FactorRosterMember } from '../../../../types/statusFactors';

export interface WeightCellProps {
  member: FactorRosterMember;
  /**
   * Commits a new influence. Present only when the caller may edit (the roster passes it for editors);
   * absent, an `editable` row renders as the read-only value. Rejecting reverts the draft and shows the error.
   */
  onCommit?: (influence: number) => Promise<void>;
}

/**
 * WEIGHT: a single em dash when the factor counts members equally (`na`, D83), a lock glyph when the
 * weight is set elsewhere, and — for an `editable` row with `onCommit` — an inline number input that commits
 * on Enter, blur or Tab and reverts on Escape. A weight of 0 is a real value: it renders as `0`, never dimmed.
 */
const WeightCell: React.FC<WeightCellProps> = ({ member, onCommit }) => {
  const { t } = useTranslation();
  const saved = member.influence ?? null;
  const [draft, setDraft] = useState<number | null>(saved);
  // PrimeReact reports the typed value from its own keydown/blur handlers, which run just before this cell's
  // bubbling handlers; the ref carries that value to `commit` without waiting for a re-render.
  const draftRef = useRef<number | null>(saved);
  // Bumped on revert so the input remounts and drops text PrimeReact has not yet reported.
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const cellRef = useRef<HTMLSpanElement>(null);
  const refocusAfterRevert = useRef(false);

  const updateDraft = (value: number | null) => {
    draftRef.current = value;
    setDraft(value);
  };

  const lastSaved = useRef(saved);
  useEffect(() => {
    if (lastSaved.current === saved) return;
    lastSaved.current = saved;
    draftRef.current = saved;
    setDraft(saved);
    setInputKey((key) => key + 1);
  }, [saved]);

  useEffect(() => {
    if (!refocusAfterRevert.current) return;
    refocusAfterRevert.current = false;
    cellRef.current?.querySelector('input')?.focus();
  }, [inputKey]);

  if (member.hidden) return null;

  if (member.weightMode === 'na') {
    return (
      <span className="rollup-roster__weight rollup-roster__weight--na" data-testid="roster-cell-weight">
        —
      </span>
    );
  }

  const memberName = member.sphereName ?? member.sphereId;

  if (member.weightMode === 'editable' && onCommit) {
    const commit = async () => {
      const next = draftRef.current;
      if (busy) return;
      if (next === null || next === saved) {
        if (next === null) {
          updateDraft(saved);
          setInputKey((key) => key + 1);
        }
        return;
      }
      setBusy(true);
      setFailed(false);
      try {
        await onCommit(next);
      } catch {
        updateDraft(saved);
        setInputKey((key) => key + 1);
        setFailed(true);
      } finally {
        setBusy(false);
      }
    };
    const revert = () => {
      refocusAfterRevert.current = true;
      updateDraft(saved);
      setInputKey((key) => key + 1);
      setFailed(false);
    };

    return (
      <span
        ref={cellRef}
        className="rollup-roster__weight rollup-roster__weight--editable"
        data-testid="roster-cell-weight"
        aria-busy={busy}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          } else if (event.key === 'Escape') {
            revert();
          }
        }}
        onBlur={() => void commit()}
      >
        <InputNumberAtom
          key={inputKey}
          value={draft}
          onChange={(value) => {
            setFailed(false);
            updateDraft(value);
          }}
          type="decimal"
          min={0}
          step={0.1}
          precision={2}
          size="compact"
          disabled={busy}
          invalid={failed}
          ariaLabel={t('statusFactors.roster.weightEdit.ariaLabel', 'Weight for {{member}}', { member: memberName })}
          className="rollup-roster__weight-input"
        />
        {failed && (
          <span role="alert" className="rollup-roster__weight-error" data-testid="roster-weight-error">
            {t('statusFactors.roster.weightEdit.failed', "Couldn't save the weight — the previous value is back.")}
          </span>
        )}
      </span>
    );
  }

  const locked = member.weightMode === 'locked';
  let tooltip: string | undefined;
  if (locked) {
    tooltip =
      member.weightSource === 'role'
        ? t('statusFactors.roster.weightRoleLinked', 'Role-linked — set on the role')
        : t('statusFactors.roster.weightLocked', 'Locked');
  }

  return (
    <span className="rollup-roster__weight" data-testid="roster-cell-weight" title={tooltip}>
      {locked && <i className="pi pi-lock rollup-roster__weight-lock" data-testid="roster-weight-lock" aria-hidden="true" />}
      <span className="rollup-roster__weight-value">{member.influence ?? ''}</span>
    </span>
  );
};

export default WeightCell;
